import React, { useState, useRef, useEffect } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import './Chat.css';

const Chat = ({
  messages = [],
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  currentUserId,
  socketId,
  isOpen = true,
  onClose,
  mode = "panel",
  variant = "meeting",
  isUploading = false,
  onFileSelect,
  files = []
}) => {
  const [inputValue, setInputValue] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && onSendMessage) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const isCompact = variant === "meeting";
  const isPanel = mode === "panel";

  if (!isOpen && isPanel) return null;

  const panelClass = `chat-panel ${variant} ${isPanel ? 'panel-mode' : 'page-mode'}`;

  return (
    <div className={panelClass}>
      {isPanel && (
        <div className="chat-header">
          <h3>Chat</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>Aucun message envoyé</p>
            <p className="hint">Commencer la conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwnMessage = String(msg.senderId) === String(currentUserId) || String(msg.iduser) === String(currentUserId);
            const isEditing = editingMessageId === (msg.id || msg.idmessage);

            const handleStartEdit = () => {
              setEditingMessageId(msg.id || msg.idmessage);
              setEditingMessageContent(msg.message || '');
            };

            const handleCancelEdit = () => {
              setEditingMessageId(null);
              setEditingMessageContent('');
            };

            const handleSaveEdit = async () => {
              if (editingMessageContent.trim() && onEditMessage) {
                try {
                  await onEditMessage(msg.id || msg.idmessage, editingMessageContent.trim());
                } catch (err) {
                  console.error('Erreur modification message:', err);
                }
              }
              setEditingMessageId(null);
              setEditingMessageContent('');
            };

            const handleDelete = async () => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
                try {
                  await onDeleteMessage(msg.id || msg.idmessage);
                } catch (err) {
                  console.error('Erreur suppression message:', err);
                }
              }
            };

            return (
              <div
                key={msg.id || msg.idmessage}
                className={`message ${
                  msg.type === 'system'
                    ? 'system'
                    : isOwnMessage
                      ? 'own'
                      : 'other'
                } ${isCompact ? 'compact' : ''}`}
              >
                {msg.type !== 'system' && (
                  <div className="message-header">
                    <span className="sender-name">{msg.senderName}</span>
                    <span className="message-time">{formatTime(msg.timestamp)}</span>
                    {isOwnMessage && (
                      <span className="message-actions">
                        <button
                          type="button"
                          className="btn-icon btn-edit-msg"
                          onClick={handleStartEdit}
                          title="Modifier le message"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-delete-msg"
                          onClick={handleDelete}
                          title="Supprimer le message"
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    )}
                  </div>
                )}
                {isEditing ? (
                  <div className="message-edit">
                    <input
                      type="text"
                      value={editingMessageContent}
                      onChange={(e) => setEditingMessageContent(e.target.value)}
                      className="edit-input"
                    />
                    <div className="edit-actions">
                      <button type="button" onClick={handleCancelEdit} className="btn-cancel-edit">Annuler</button>
                      <button type="button" onClick={handleSaveEdit} className="btn-save-edit">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <div className="message-content">
                    {msg.message}
                    {msg.edited && <span className="edited-indicator">(modifié)</span>}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {files && files.length > 0 && (
        <div className="chat-files">
          <div className="files-list">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <a
                  href={`${process.env.REACT_APP_API_URL}/api/fichiers/download/${encodeURIComponent(file.url.split('/').pop())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  <span className="file-icon">📎</span>
                  <span className="file-name">{file.nomfichier || file.url.split('/').pop()}</span>
                  <span className="file-size">{formatFileSize(file.taille)}</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <form className="chat-input-form" onSubmit={handleSubmit}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileSelect}
          style={{ display: 'none' }}
          id="file-upload"
        />
        <label htmlFor="file-upload" className="file-upload-btn" title="Envoyer un fichier">
          {isUploading ? '⏳' : '📎'}
        </label>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={isCompact ? "Message..." : "Tapez un message..."}
          className="chat-input"
        />
        <button type="submit" className="send-btn" disabled={!inputValue.trim()}>
          ➤
        </button>
      </form>
    </div>
  );
};

export default Chat;